import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { brandApi } from '@/api/account-management/brandApi';
import { type Location } from '@/models/account-management/Location';
import { useAppContext } from '../AppContext';
import { useCurrentUser } from '../providers/currentUser/CurrentUserProvider';
import { assignedLocations, type Assignment } from './locationScope';

/**
 * The signed-in user's location assignment, and whether it applies to them at all.
 *
 * One place, because the rule has three parts and every screen that reimplemented it would get to choose
 * which two it remembered:
 *
 * - **Super admins are not scoped.**
 * - **A response with no `locationIds` at all is not scoped either.** That is an API older than this build;
 *   reading "absent" as "assigned to nothing" would empty every list for every user the moment the portal
 *   deployed ahead of the API. An empty *array* is different — that is the API saying none, and none means
 *   none.
 * - **Undecided counts as restricted.** While the current user is still loading we do not know the
 *   assignment, and showing the full list for that moment flashes exactly what this exists to hide.
 *   `assigned` is undefined then, so the filters return undefined and callers read it as still loading.
 */
export const useAssignment = (): { assigned: Assignment; restrict: boolean } => {
    const app = useAppContext();
    const { user, isLoading } = useCurrentUser();
    const superAdmin = app.isSuperAdmin();

    const assigned = useMemo<Assignment>(
        () => (user?.locationIds ? new Set(user.locationIds) : undefined),
        [user]
    );

    return { assigned, restrict: !superAdmin && (isLoading || !!assigned) };
};

/**
 * A brand's locations, restricted to the ones the signed-in user may work in.
 *
 * Opt-in rather than baked into the query, because the same `['locations', brandId]` cache entry feeds
 * screens that must NOT be filtered — above all the Locations tab of the user editor, where an admin is
 * choosing which locations to GRANT someone. Filtering that one would mean you could only ever hand out
 * access you already held, and nobody could bootstrap a new user's first location.
 *
 * `enabled` gates the fetch for dialogs that should not load until they are open.
 */
export const useAssignedLocations = (
    brandId: string | null | undefined,
    options?: { enabled?: boolean }
): { locations: Location[] | undefined; isLoading: boolean } => {
    const { assigned, restrict } = useAssignment();

    const { data, isLoading } = useQuery<Location[]>({
        queryKey: ['locations', brandId ?? ''],
        enabled: (options?.enabled ?? true) && !!brandId,
        queryFn: () => brandApi.listLocations(brandId!),
        staleTime: Infinity,
    });

    return {
        locations: restrict ? assignedLocations(data, assigned) : data,
        // Still loading while the assignment is unknown: the filtered list is undefined until it lands, and a
        // caller that stopped showing a spinner before then would render an empty list as though it were the
        // answer.
        isLoading: isLoading || (restrict && !assigned),
    };
};
